import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export interface DayHours {
  enabled: boolean
  start: string
  end: string
}

export type DayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export type WorkingHours = Record<DayKey, DayHours>

interface TimeSlot {
  id: string
  days: DayKey[]
  start: string
  end: string
}

const ALL_DAYS: DayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

interface Props {
  value: WorkingHours
  onChange: (value: WorkingHours) => void
}

/**
 * Éditeur d'horaires basé sur des "plages" (slots) — chaque slot regroupe
 * plusieurs jours qui partagent les mêmes horaires.
 * État interne (timeSlots) reconstruit depuis la prop value à l'init, puis
 * propagé via onChange en tant que WorkingHours.
 */
export function WorkingHoursEditor({ value, onChange }: Props) {
  const { t } = useTranslation()
  const [sameHoursEveryday, setSameHoursEveryday] = useState(false)

  const slotsFromValue = (): TimeSlot[] => {
    const slots: TimeSlot[] = []
    const processed = new Set<DayKey>()
    ALL_DAYS.forEach((day) => {
      if (processed.has(day) || !value[day]?.enabled) return
      const hours = value[day]
      const sameDays: DayKey[] = [day]
      processed.add(day)
      ALL_DAYS.forEach((other) => {
        if (
          other !== day &&
          !processed.has(other) &&
          value[other]?.enabled &&
          value[other].start === hours.start &&
          value[other].end === hours.end
        ) {
          sameDays.push(other)
          processed.add(other)
        }
      })
      slots.push({
        id: `slot-${Date.now()}-${day}`,
        days: sameDays,
        start: hours.start,
        end: hours.end,
      })
    })
    return slots
  }

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(() => slotsFromValue())

  const toWorkingHours = (slots: TimeSlot[]): WorkingHours => {
    const out: WorkingHours = ALL_DAYS.reduce(
      (acc, day) => ({ ...acc, [day]: { enabled: false, start: '', end: '' } }),
      {} as WorkingHours,
    )
    slots.forEach((slot) => {
      slot.days.forEach((day) => {
        out[day] = { enabled: true, start: slot.start, end: slot.end }
      })
    })
    return out
  }

  const updateSlots = (next: TimeSlot[]) => {
    setTimeSlots(next)
    onChange(toWorkingHours(next))
  }

  const handleSlotChange = (
    id: string,
    field: 'start' | 'end',
    newValue: string,
  ) => {
    updateSlots(
      timeSlots.map((slot) =>
        slot.id === id ? { ...slot, [field]: newValue } : slot,
      ),
    )
  }

  const handleDayToggle = (slotId: string, day: DayKey) => {
    const slot = timeSlots.find((s) => s.id === slotId)
    if (!slot) return
    if (slot.days.includes(day)) {
      const newDays = slot.days.filter((d) => d !== day)
      if (newDays.length === 0) {
        updateSlots(timeSlots.filter((s) => s.id !== slotId))
      } else {
        updateSlots(
          timeSlots.map((s) => (s.id === slotId ? { ...s, days: newDays } : s)),
        )
      }
    } else {
      // un jour ne peut appartenir qu'à une plage à la fois
      updateSlots(
        timeSlots.map((s) =>
          s.id === slotId
            ? { ...s, days: [...s.days, day] }
            : { ...s, days: s.days.filter((d) => d !== day) },
        ),
      )
    }
  }

  const handleDelete = (id: string) =>
    updateSlots(timeSlots.filter((s) => s.id !== id))

  const handleAddSlot = () =>
    setTimeSlots((prev) => [
      ...prev,
      { id: `slot-${Date.now()}`, days: [], start: '09:00', end: '17:00' },
    ])

  const handleSameHoursToggle = (checked: boolean) => {
    setSameHoursEveryday(checked)
    if (checked && timeSlots.length > 0) {
      const first = timeSlots[0]
      updateSlots([{ ...first, days: [...ALL_DAYS] }])
    }
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Label
            htmlFor="same-hours"
            className="text-sm text-gray-600 dark:text-slate-400 cursor-pointer"
          >
            {t('manager.aiAgents.workingHours.sameAllWeek')}
          </Label>
          <Switch
            id="same-hours"
            checked={sameHoursEveryday}
            onCheckedChange={handleSameHoursToggle}
          />
        </div>
      </div>

      <div className="space-y-4">
        {timeSlots.map((slot) => (
          <div
            key={slot.id}
            className="group relative border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:border-blue-400 transition-colors"
          >
            <div className="flex flex-wrap gap-2 mb-3">
              {ALL_DAYS.map((day) => {
                const isSelected = slot.days.includes(day)
                const isUsedElsewhere = timeSlots.some(
                  (s) => s.id !== slot.id && s.days.includes(day),
                )
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(slot.id, day)}
                    disabled={isUsedElsewhere}
                    className={cn(
                      'px-2 py-1 text-xs font-bold rounded-md transition-all',
                      isSelected
                        ? 'bg-blue-600 text-white shadow-sm cursor-pointer'
                        : isUsedElsewhere
                          ? 'bg-gray-50 dark:bg-slate-800 text-gray-300 cursor-not-allowed opacity-50'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-400 hover:bg-gray-200 cursor-pointer',
                    )}
                    title={
                      isUsedElsewhere
                        ? t('manager.aiAgents.workingHours.dayInUse', {
                            day: t(`manager.aiAgents.workingHours.days.${day}`),
                          })
                        : undefined
                    }
                  >
                    {t(`manager.aiAgents.workingHours.daysShort.${day}`)}
                  </button>
                )
              })}
            </div>

            <div className="flex items-center gap-3">
              <Input
                type="time"
                value={slot.start}
                onChange={(e) => handleSlotChange(slot.id, 'start', e.target.value)}
                className="flex-1 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-400 font-medium">-</span>
              <Input
                type="time"
                value={slot.end}
                onChange={(e) => handleSlotChange(slot.id, 'end', e.target.value)}
                className="flex-1 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => handleDelete(slot.id)}
                className="text-gray-400 hover:text-red-500 p-2 transition-colors"
                title={t('manager.aiAgents.workingHours.deleteSlot')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="ghost"
        onClick={handleAddSlot}
        className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 mt-4"
      >
        <Plus className="w-4 h-4" />
        {t('manager.aiAgents.workingHours.addSlot')}
      </Button>
    </div>
  )
}

/** Valeur par défaut Lun-Ven 09:00-17:00. */
export function defaultWorkingHours(): WorkingHours {
  return {
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '', end: '' },
    sunday: { enabled: false, start: '', end: '' },
  }
}
